import React from 'react';
import { 
  FolderKanban, 
  CheckSquare, 
  Package, 
  Users, 
  BarChart3, 
  Shield, 
  Zap, 
  Globe,
  Clock,
  Smartphone
} from 'lucide-react';

const Features = () => {
  const mainFeatures = [
    {
      icon: FolderKanban,
      title: 'Project Management',
      description: 'Plan, track, and deliver projects on time with powerful project management tools, Gantt charts, and team collaboration features.',
      color: 'blue'
    },
    {
      icon: CheckSquare,
      title: 'Task Management',
      description: 'Organize tasks, set priorities, assign team members, and track progress with intuitive task boards and automation.',
      color: 'purple'
    },
    {
      icon: Package,
      title: 'Asset Management',
      description: 'Track and manage all your digital and physical assets with detailed inventory, maintenance schedules, and depreciation tracking.',
      color: 'green'
    },
    {
      icon: Users,
      title: 'HR Management',
      description: 'Streamline HR processes including employee onboarding, time tracking, payroll, performance reviews, and benefits management.',
      color: 'orange'
    },
    {
      icon: BarChart3,
      title: 'Analytics & Reporting',
      description: 'Get deep insights with customizable dashboards, real-time analytics, and comprehensive reporting across all modules.',
      color: 'indigo'
    },
    {
      icon: Shield,
      title: 'Security & Compliance',
      description: 'Enterprise-grade security with role-based access control, data encryption, and compliance with industry standards.',
      color: 'red'
    }
  ];

  const additionalFeatures = [
    { icon: Zap, title: 'Automation', description: 'Automate repetitive tasks and workflows' },
    { icon: Globe, title: 'Global Access', description: 'Access from anywhere, anytime' },
    { icon: Clock, title: 'Real-time Sync', description: 'Instant updates across all devices' },
    { icon: Smartphone, title: 'Mobile Apps', description: 'Native iOS and Android apps' }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'from-blue-500 to-blue-600',
      purple: 'from-purple-500 to-purple-600',
      green: 'from-green-500 to-green-600',
      orange: 'from-orange-500 to-orange-600',
      indigo: 'from-indigo-500 to-indigo-600',
      red: 'from-red-500 to-red-600'
    };
    return colors[color] || colors.blue;
  };

  return (
    <section id="features" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Everything You Need in One Platform
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Stop juggling multiple tools. Our comprehensive platform brings all your business management needs under one roof.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {mainFeatures.map((feature, index) => (
            <div 
              key={index}
              className="group bg-white rounded-xl p-8 border border-gray-200 hover:border-gray-300 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${getColorClasses(feature.color)} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <feature.icon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Plus Many More Features
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalFeatures.map((feature, index) => (
              <div key={index} className="flex items-center space-x-4 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="flex-shrink-0">
                  <feature.icon className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{feature.title}</h4>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;